/**
 * generate-event-plan.js
 * Shared use case for event plan generation across services and legacy executors.
 */

const TEAM_TYPE_TO_LENS = {
  Scrum: 'scrum',
  Kanban: 'kanban',
  SAFe: 'safe',
  LeSS: 'less',
  XP: 'xp',
  Agile: 'agile',
};

export function createGenerateEventPlan({
  sendPlan,
  buildPlanPrompt,
  fetchTeamDetails = async () => null,
  mapTeamTypeToLens = defaultMapTeamTypeToLens,
  sanitizeResponse = (response) => response,
  metricsLogger = null,
  metricsStage = 'generateEventPlan',
  now = defaultNow,
  teamCache = null,
  cacheKeyResolver = defaultCacheKeyResolver,
} = {}) {
  if (typeof sendPlan !== 'function') {
    throw new Error('sendPlan is required');
  }
  if (typeof buildPlanPrompt !== 'function') {
    throw new Error('buildPlanPrompt is required');
  }

  const cache = teamCache instanceof Map ? teamCache : new Map();

  async function loadTeamContext(teamId) {
    if (!teamId) {
      return { teamInstructions: null, teamType: null, teamName: null, durationMs: 0 };
    }

    const cacheKey = cacheKeyResolver ? cacheKeyResolver(teamId) : teamId;

    if (cacheKey != null && cache.has(cacheKey)) {
      return { ...cache.get(cacheKey), durationMs: 0 };
    }

    const start = now();
    try {
      const details = await fetchTeamDetails(teamId);
      const context = {
        teamInstructions: details?.instructions ?? details?.notes ?? null,
        teamType: details?.team_type ?? details?.type ?? null,
        teamName: details?.name ?? null,
      };
      if (cacheKey != null) {
        cache.set(cacheKey, context);
      }
      return { ...context, durationMs: elapsedSince(start, now) };
    } catch (error) {
      return { teamInstructions: null, teamType: null, teamName: null, durationMs: elapsedSince(start, now) };
    }
  }

  return async function generateEventPlan({
    eventType,
    timebox,
    notes = '',
    persona = 'Agile Coach',
    lens = 'general',
    teamId = null,
    // An ART: the subject is a scope, named by the caller since it has no team record.
    scopeId = null,
    teamName = null,
    attachments = [],
    usePrecision = true,
    apmToolLink = null,
    useIntelligenceContext = false,
  } = {}) {
    const trimmedEvent = normalizeString(eventType);
    if (!trimmedEvent) {
      throw new Error('eventType is required');
    }

    const trimmedTimebox = normalizeString(timebox) || 'Unspecified duration';
    const trimmedNotes = typeof notes === 'string' ? notes.trim() : '';
    const files = Array.isArray(attachments) ? attachments.slice() : [];

    const teamContext = await loadTeamContext(teamId);
    const effectiveLens = resolveLens(lens, teamContext.teamType, mapTeamTypeToLens);

    const precisionMessage = callPlanBuilder(buildPlanPrompt, {
      usePrecision,
      eventType: trimmedEvent,
      timebox: trimmedTimebox,
      notes: trimmedNotes,
      persona,
      teamLens: effectiveLens,
      teamId,
      attachments: files,
      teamInstructions: teamContext.teamInstructions,
      teamType: teamContext.teamType,
      apmToolLink,
    });

    const submitStart = now();
    const submission = await sendPlan({
      persona,
      lens: effectiveLens,
      teamId,
      scopeId,
      teamName: teamContext.teamName || normalizeString(teamName) || null,
      eventType: trimmedEvent,
      timebox: trimmedTimebox,
      notes: trimmedNotes,
      precisionMessage,
      attachments: files,
      useIntelligenceContext: useIntelligenceContext === true,
    });
    const submitDuration = elapsedSince(submitStart, now);

    const timings = {
      teamFetchMs: teamContext.durationMs,
      submitMs: submitDuration,
    };

    if (typeof metricsLogger === 'function') {
      safeInvoke(metricsLogger, {
        stage: metricsStage,
        durationMs: submitDuration,
        teamFetchMs: teamContext.durationMs ?? null,
        payloadBytes: estimatePayloadBytes(files),
      });
    }

    return {
      response: sanitizeResponse(submission),
      precisionMessage,
      context: {
        eventType: trimmedEvent,
        timebox: trimmedTimebox,
        lens: effectiveLens,
        teamInstructions: teamContext.teamInstructions,
        teamType: teamContext.teamType,
      },
      durationMs: submitDuration,
      timings,
      tokens: submission?.tokens || submission?.usage?.total_tokens || null,
    };
  };
}

function callPlanBuilder(builder, {
  usePrecision,
  eventType,
  timebox,
  notes,
  persona,
  teamLens,
  teamId,
  attachments,
  teamInstructions,
  teamType,
  apmToolLink,
}) {
  if (builder.length >= 4) {
    return builder(eventType, timebox, notes, {
      persona,
      teamLens,
      teamId,
      files: attachments,
      teamInstructions,
      teamType,
      apmToolLink,
      usePrecision,
    });
  }

  return builder({
    usePrecision,
    eventType,
    timebox,
    notes,
    persona,
    teamLens,
    teamId,
    attachments,
    teamInstructions,
    teamType,
    apmToolLink,
  });
}

function resolveLens(currentLens = 'general', teamType = null, mapper = defaultMapTeamTypeToLens) {
  if (!teamType) {
    return currentLens || 'general';
  }
  if (currentLens && currentLens !== 'general' && currentLens !== 'auto') {
    return currentLens;
  }
  return mapper(teamType) || (typeof teamType === 'string' ? teamType.toLowerCase() : currentLens || 'general');
}

function defaultMapTeamTypeToLens(teamType) {
  if (!teamType) {
    return null;
  }
  return TEAM_TYPE_TO_LENS[teamType] || (typeof teamType === 'string' ? teamType.toLowerCase() : null);
}

function normalizeString(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
}

function defaultNow() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function elapsedSince(start, nowFn = defaultNow) {
  const end = nowFn();
  return Number.isFinite(start) ? Math.max(0, end - start) : undefined;
}

function estimatePayloadBytes(files) {
  if (!Array.isArray(files) || !files.length) {
    return 0;
  }
  return files.reduce((total, file) => total + (file?.size || 0), 0);
}

function safeInvoke(fn, payload) {
  try {
    fn(payload);
  } catch (error) {
    // Preserve legacy behaviour on metrics errors
  }
}

function defaultCacheKeyResolver(teamId) {
  return teamId || null;
}

export function createTeamContextLoader(fetchTeamDetails, teamCache = new Map(), now = defaultNow, cacheKeyResolver = defaultCacheKeyResolver) {
  return async function load(teamId) {
    if (!teamId) {
      return { teamInstructions: null, teamType: null, teamName: null, durationMs: 0 };
    }

    const cacheKey = cacheKeyResolver ? cacheKeyResolver(teamId) : teamId;
    if (cacheKey != null && teamCache.has(cacheKey)) {
      return { ...teamCache.get(cacheKey), durationMs: 0 };
    }

    const start = now();
    try {
      const details = await fetchTeamDetails(teamId);
      const context = {
        teamInstructions: details?.instructions ?? details?.notes ?? null,
        teamType: details?.team_type ?? details?.type ?? null,
        teamName: details?.name ?? null,
      };
      if (cacheKey != null) {
        teamCache.set(cacheKey, context);
      }
      return { ...context, durationMs: elapsedSince(start, now) };
    } catch (error) {
      return { teamInstructions: null, teamType: null, teamName: null, durationMs: elapsedSince(start, now) };
    }
  };
}

if (typeof window !== 'undefined') {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.event = window.AgileProDomain.event || {};
  window.AgileProDomain.event.createGenerateEventPlan = createGenerateEventPlan;
}
